import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import { Layout, Menu, Avatar, Dropdown, Breadcrumb, Tag, Badge, Drawer, List, Button, Tabs, Empty, Space } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  ExportOutlined,
  BellOutlined,
  CheckOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../auth/AuthContext';
import { ROLE_INFO, GROUP_MENU_ITEMS, SUBSIDIARY_MENU_ITEMS } from '../../auth/constants';
import { getNotificationsForUser } from '../../mock/notifications';
import logo from '../../assets/logo.png';
import './index.css';

const { Sider, Header, Content } = Layout;

const breadcrumbNameMap = {
  '/': '管理驾驶舱',
  '/coding': '统一编码管理',
  '/coding/mapping': '批量导入',
  '/blacklist': '黑名单管控',
  '/archive': '供应商档案',
  '/archive/detail': '档案详情',
  '/admission': '准入管理',
  '/admission/new': '新建准入',
  '/admission/inspection': '现场考察',
  '/supplier-suspension': '资格中止管理',
  '/performance': '履约管理',
  '/performance/inspection': '巡检抽检',
  '/performance/issues': '问题跟踪',
  '/supervision': '监督合规',
  '/supervision/related-party': '关联方排查',
  '/supervision/multi-dept': '多部门视图',
  '/evaluation': '评价分级',
  '/evaluation/template': '评价模板',
  '/evaluation/daily': '日常评价',
  '/evaluation/annual': '年度评价',
  '/evaluation/grading': '分级结果',
  '/exit': '退出管理',
  '/exit/apply': '退出申请',
  '/risk': '风险预警',
  '/approval': '审批中心',
  '/audit-log': '操作记录',
  '/announcement': '公告管理',
};

const AdminLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isGroup, isSubsidiary } = useAuth();
  const [collapsed, setCollapsed] = React.useState(false);
  const [notifs, setNotifs] = useState(() => getNotificationsForUser(user));
  const [notifDrawerVisible, setNotifDrawerVisible] = useState(false);

  // 角色切换（重新登录为不同角色）时刷新对应的消息通知
  React.useEffect(() => {
    setNotifs(getNotificationsForUser(user));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, user?.subsidiary]);

  const unreadCount = notifs.filter((n) => !n.read).length;

  const handleNotifClick = (item) => {
    setNotifs((prev) => prev.map((n) => n.id === item.id ? { ...n, read: true } : n));
    if (item.link) {
      setNotifDrawerVisible(false);
      navigate(item.link);
    }
  };

  const handleMarkAllRead = () => {
    setNotifs((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const handleClearRead = () => {
    setNotifs((prev) => prev.filter((n) => !n.read));
  };

  const menuItems = isGroup ? GROUP_MENU_ITEMS : SUBSIDIARY_MENU_ITEMS;

  const selectedKeys = useMemo(() => {
    const path = location.pathname;
    if (path === '/') return ['/'];
    return [path];
  }, [location.pathname]);

  const openKeys = useMemo(() => {
    const path = location.pathname;
    if (path === '/') return [];
    const segments = path.split('/').filter(Boolean);
    if (segments.length >= 1) {
      const root = '/' + segments[0];
      const parentMap = {
        '/coding': '/coding',
        '/coding/mapping': '/admission',
        '/archive': '/archive',
        '/archive/detail': '/archive',
        '/admission': '/admission',
        '/supplier-suspension': '/archive',
        '/performance': '/performance',
        '/supervision': '/supervision',
        '/evaluation': '/evaluation',
        '/exit': '/exit',
      };
      const parentKey = parentMap[path] || root;
      return [parentKey];
    }
    return [];
  }, [location.pathname]);

  const breadcrumbItems = useMemo(() => {
    const pathSnippets = location.pathname.split('/').filter(Boolean);
    const items = [
      { title: '首页', href: '/' },
    ];
    let currentPath = '';
    pathSnippets.forEach((snippet) => {
      currentPath += '/' + snippet;
      const name = breadcrumbNameMap[currentPath];
      if (name) {
        items.push({ title: name });
      }
    });
    return items;
  }, [location.pathname]);

  const handleMenuClick = ({ key }) => {
    navigate(key);
  };

  const roleInfo = ROLE_INFO[user?.role] || {};

  const userMenuItems = [
    {
      key: 'logout',
      icon: <ExportOutlined />,
      label: '退出登录',
    },
  ];

  const handleUserMenu = ({ key }) => {
    if (key === 'logout') {
      logout();
      navigate('/login');
    }
  };

  return (
    <Layout className="admin-layout">
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={220}
        className="admin-sider"
      >
        <div className="admin-logo">
          {collapsed ? (
            <img src={logo} alt="中国资源循环集团" className="admin-logo-img-collapsed" />
          ) : (
            <img src={logo} alt="中国资源循环集团供应商管理系统" className="admin-logo-img" />
          )}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={selectedKeys}
          defaultOpenKeys={openKeys}
          items={menuItems}
          onClick={handleMenuClick}
          className="admin-menu"
        />
      </Sider>
      <Layout>
        <Header className="admin-header">
          <div className="admin-header-left">
            {React.createElement(
              collapsed ? MenuUnfoldOutlined : MenuFoldOutlined,
              {
                className: 'admin-trigger',
                onClick: () => setCollapsed(!collapsed),
              }
            )}
            <Breadcrumb items={breadcrumbItems} className="admin-breadcrumb" />
          </div>
          <div className="admin-header-right">
            <Badge count={unreadCount} size="small" offset={[-2, 2]}>
              <BellOutlined
                className="admin-trigger"
                style={{ fontSize: 18 }}
                onClick={() => setNotifDrawerVisible(true)}
              />
            </Badge>
            <Dropdown
              menu={{ items: userMenuItems, onClick: handleUserMenu }}
              placement="bottomRight"
            >
              <div className="admin-user-info">
                <Avatar size={32} icon={<UserOutlined />} className="admin-avatar" />
                <div className="admin-user-detail">
                  <span className="admin-user-name">{user?.name || '用户'}</span>
                  <span className="admin-user-role">
                    <Tag color={roleInfo.color} style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px', marginRight: 0 }}>
                      {roleInfo.label}
                    </Tag>
                    {isSubsidiary && user?.orgPath?.length > 1 && (
                      <span style={{ fontSize: 11, color: '#999', marginLeft: 4 }}>
                        {user.orgPath.slice(1).join(' / ')}
                      </span>
                    )}
                  </span>
                </div>
              </div>
            </Dropdown>
          </div>
        </Header>
        <Content className="admin-content">
          <Outlet />
        </Content>
      </Layout>

      {/* Notification Drawer */}
      <Drawer
        title="消息通知"
        placement="right"
        onClose={() => setNotifDrawerVisible(false)}
        open={notifDrawerVisible}
        width={420}
        extra={
          <Space>
            <Button size="small" type="link" icon={<CheckOutlined />} onClick={handleMarkAllRead}>全部已读</Button>
            <Button size="small" type="link" danger icon={<DeleteOutlined />} onClick={handleClearRead}>清除已读</Button>
          </Space>
        }
      >
        <Tabs
          items={[
            {
              key: 'all',
              label: `全部 (${notifs.length})`,
              children: (
                <List
                  dataSource={notifs}
                  renderItem={(item) => {
                    const typeColor = {
                      approval: 'blue', warning: 'orange', risk: 'red', info: 'cyan', system: 'default',
                    }[item.type] || 'default';
                    const typeLabel = {
                      approval: '审批', warning: '预警', risk: '风险', info: '通知', system: '系统',
                    }[item.type] || '通知';
                    return (
                      <List.Item
                        style={{
                          background: item.read ? 'transparent' : '#fff1f0',
                          padding: '12px 16px',
                          borderRadius: 6,
                          cursor: 'pointer',
                          marginBottom: 8,
                        }}
                        onClick={() => handleNotifClick(item)}
                      >
                        <List.Item.Meta
                          title={
                            <span>
                              <Tag color={typeColor} style={{ marginRight: 8 }}>{typeLabel}</Tag>
                              {item.title}
                            </span>
                          }
                          description={
                            <div>
                              <div style={{ color: '#666', marginTop: 4 }}>{item.content}</div>
                              <div style={{ color: '#999', fontSize: 12, marginTop: 4 }}>{item.date}</div>
                            </div>
                          }
                        />
                      </List.Item>
                    );
                  }}
                />
              ),
            },
            {
              key: 'unread',
              label: `未读 (${unreadCount})`,
              children: notifs.filter((n) => !n.read).length === 0 ? (
                <Empty description="暂无未读消息" />
              ) : (
                <List
                  dataSource={notifs.filter((n) => !n.read)}
                  renderItem={(item) => {
                    const typeColor = {
                      approval: 'blue', warning: 'orange', risk: 'red', info: 'cyan', system: 'default',
                    }[item.type] || 'default';
                    const typeLabel = {
                      approval: '审批', warning: '预警', risk: '风险', info: '通知', system: '系统',
                    }[item.type] || '通知';
                    return (
                      <List.Item
                        style={{ background: '#fff1f0', padding: '12px 16px', borderRadius: 6, cursor: 'pointer', marginBottom: 8 }}
                        onClick={() => handleNotifClick(item)}
                      >
                        <List.Item.Meta
                          title={<span><Tag color={typeColor} style={{ marginRight: 8 }}>{typeLabel}</Tag>{item.title}</span>}
                          description={
                            <div>
                              <div style={{ color: '#666', marginTop: 4 }}>{item.content}</div>
                              <div style={{ color: '#999', fontSize: 12, marginTop: 4 }}>{item.date}</div>
                            </div>
                          }
                        />
                      </List.Item>
                    );
                  }}
                />
              ),
            },
          ]}
        />
      </Drawer>
    </Layout>
  );
};

export default AdminLayout;
